import React from 'react';
import { Link } from 'react-router-dom';
import { services } from '../data/services';
import { 
  ChevronRight,
  Phone,
  Container,
  Recycle,
  ShieldCheck,
  Mountain,
  Droplets,
  Factory,
  ArrowRight
} from 'lucide-react';
import { binImages } from '../data/mock';

const iconMap = {
  Container,
  Recycle,
  ShieldCheck,
  Mountain,
  Droplets,
  Factory
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={binImages.industrial}
            alt="Our Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#1a365d]/90 to-[#1a365d]/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="flex items-center space-x-2 text-white/70 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Services</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Our Services
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mb-8">
            Comprehensive environmental and waste management solutions for residential, 
            commercial, and industrial clients across Ontario.
          </p>

          <a
            href="tel:(905) 595-6987"
            className="inline-flex items-center space-x-2 bg-white text-[#1a365d] font-semibold px-8 py-4 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <Phone className="h-5 w-5" />
            <span>Call (905) 595-6987</span>
          </a>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold tracking-wider uppercase text-sm">What We Offer</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mt-4 mb-6">
              Full-Service Environmental Solutions
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              From bin rentals to complex remediation projects, we have the expertise, 
              equipment, and team to handle your environmental needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.heroImage}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d]/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">{service.title}</h3>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-gray-600 mb-4">{service.shortDescription}</p>
                    
                    <ul className="space-y-2 mb-6">
                      {service.highlights.slice(0, 3).map((h, idx) => (
                        <li key={idx} className="text-sm text-gray-500 flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center justify-between">
                      {service.startingPrice ? (
                        <div>
                          <span className="text-xs text-gray-500">Starting at</span>
                          <div className="text-xl font-bold text-[#1a365d]">${service.startingPrice}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Custom pricing</span>
                      )}
                      <span className="flex items-center text-blue-600 font-medium text-sm group-hover:text-[#1a365d] transition-colors">
                        Learn more
                        <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-600 font-semibold tracking-wider uppercase text-sm">Why Renewed Environmental</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mt-4 mb-6">
                Your Trusted Partner for Environmental Services
              </h2>
              <p className="text-gray-600 mb-8">
                With over a decade of experience serving Ontario, we've built our reputation 
                on reliability, expertise, and exceptional customer service.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-[#1a365d] mb-2">10+</div>
                  <div className="text-gray-600">Years of Experience</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-[#1a365d] mb-2">5,000+</div>
                  <div className="text-gray-600">Projects Completed</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-[#1a365d] mb-2">100%</div>
                  <div className="text-gray-600">Licensed & Insured</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-[#1a365d] mb-2">24/7</div>
                  <div className="text-gray-600">Emergency Response</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <img src={binImages.residential} alt="Service" className="w-full h-48 object-cover rounded-xl shadow-lg" />
              <img src={binImages.commercial} alt="Service" className="w-full h-48 object-cover rounded-xl shadow-lg mt-8" />
              <img src={binImages.construction} alt="Service" className="w-full h-48 object-cover rounded-xl shadow-lg" />
              <img src={binImages.industrial} alt="Service" className="w-full h-48 object-cover rounded-xl shadow-lg mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#1a365d] to-[#2d5a9e] rounded-3xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Need Help Choosing a Service?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
              Our team is ready to discuss your project and recommend the best solution.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:(905) 595-6987"
                className="flex items-center space-x-2 bg-white text-[#1a365d] font-semibold px-8 py-4 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <Phone className="h-5 w-5" />
                <span>Call (905) 595-6987</span>
              </a>
              <a
                href="mailto:info@renewedenv.com"
                className="flex items-center space-x-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/20 transition-colors border border-white/30"
              >
                <span>Send an Email</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
